/**
 * Client-side mirror of backend/app/core/website_validator.py.
 * This is a UX pre-check only — the backend is the source of truth and
 * re-validates on submit, so this file must stay format/security-equivalent
 * but never needs to be "more correct" than the server.
 */

const ALLOWED_SCHEMES = new Set(["http:", "https:"]);
const DEFAULT_PORTS: Record<string, string> = { "http:": "80", "https:": "443" };

const LABEL_RE = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?$/;

const BLOCKED_HOSTNAME_SUFFIXES = [".local", ".internal", ".localhost"];
const BLOCKED_HOSTNAMES = new Set(["localhost", "localdomain", "metadata.google.internal"]);
const METADATA_IPS = new Set(["169.254.169.254"]);

export interface WebsiteValidationResult {
  valid: boolean;
  normalized?: string;
  error?: string;
}

function isIPv4(host: string): boolean {
  return /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(host);
}

function isPrivateOrReservedIPv4(host: string): boolean {
  const parts = host.split(".").map(Number);
  if (parts.some((p) => Number.isNaN(p) || p < 0 || p > 255)) return true; // malformed → reject
  const [a, b] = parts;
  if (a === 10) return true; // 10.0.0.0/8
  if (a === 127) return true; // loopback
  if (a === 0) return true; // "this" network
  if (a === 169 && b === 254) return true; // link-local / metadata
  if (a === 172 && b >= 16 && b <= 31) return true; // 172.16.0.0/12
  if (a === 192 && b === 168) return true; // 192.168.0.0/16
  if (a === 100 && b >= 64 && b <= 127) return true; // carrier-grade NAT
  if (a >= 224) return true; // multicast/reserved
  return METADATA_IPS.has(host);
}

function isIPv6Literal(host: string): boolean {
  // urlHostname strips brackets already; a bare IPv6 has colons.
  return host.includes(":");
}

function looksLikePrivateIPv6(host: string): boolean {
  const h = host.toLowerCase();
  return h === "::1" || h === "::" || h.startsWith("fe80:") || h.startsWith("fc") || h.startsWith("fd");
}

function validateDomain(host: string): { ok: boolean; error?: string; normalized?: string } {
  if (!host) return { ok: false, error: "Invalid URL format: missing host." };
  if (host.length > 253) return { ok: false, error: "Invalid domain: exceeds 253 characters." };

  const lower = host.toLowerCase();
  if (BLOCKED_HOSTNAMES.has(lower) || BLOCKED_HOSTNAME_SUFFIXES.some((s) => lower.endsWith(s))) {
    return { ok: false, error: "Private or local address not allowed." };
  }

  let asciiHost = lower;
  try {
    // IDN → punycode. Browsers expose this via the URL API's hostname
    // already (it auto-converts), so `lower` is typically already ASCII;
    // this branch mainly guards against malformed unicode.
    asciiHost = lower;
  } catch {
    return { ok: false, error: "Invalid domain: not a valid hostname." };
  }

  const labels = asciiHost.split(".");
  if (labels.length < 2) {
    return { ok: false, error: "Invalid domain: a public top-level domain is required." };
  }
  for (const label of labels) {
    if (!label || label.length > 63) {
      return { ok: false, error: "Invalid domain: label length out of range." };
    }
    if (label.startsWith("-") || label.endsWith("-")) {
      return { ok: false, error: "Invalid domain: labels cannot start or end with a hyphen." };
    }
    if (!LABEL_RE.test(label)) {
      return { ok: false, error: "Invalid domain: labels may only contain letters, digits, and hyphens." };
    }
  }
  const tld = labels[labels.length - 1];
  if (!/^[a-zA-Z]+$/.test(tld) || tld.length < 2) {
    return { ok: false, error: "Invalid domain: missing a valid public TLD." };
  }
  return { ok: true, normalized: lower };
}

/** Validate + normalize a seller-submitted website URL (client-side pre-check). */
export function validateWebsite(raw: string): WebsiteValidationResult {
  if (raw == null) return { valid: true, normalized: undefined };
  let s = raw.trim();
  if (!s) return { valid: true, normalized: undefined };

  // eslint-disable-next-line no-control-regex
  if (/[\x00-\x1f]/.test(s)) {
    return { valid: false, error: "Invalid URL format." };
  }

  if (!/:\/\//.test(s)) {
    s = `https://${s}`;
  }

  let url: URL;
  try {
    url = new URL(s);
  } catch {
    return { valid: false, error: "Invalid URL format." };
  }

  if (!ALLOWED_SCHEMES.has(url.protocol)) {
    return { valid: false, error: "Unsupported protocol: only http/https URLs are allowed." };
  }
  if (url.username || url.password) {
    return { valid: false, error: "Invalid URL format: credentials are not allowed in a website URL." };
  }

  let host = url.hostname;
  if (host.startsWith("[") && host.endsWith("]")) host = host.slice(1, -1);

  if (isIPv6Literal(host)) {
    return { valid: false, error: "Private or local address not allowed." };
  }
  if (isIPv4(host)) {
    if (isPrivateOrReservedIPv4(host)) {
      return { valid: false, error: "Private or local address not allowed." };
    }
    return { valid: false, error: "Invalid domain: IP-literal hosts are not accepted, use a domain name." };
  }
  if (looksLikePrivateIPv6(host)) {
    return { valid: false, error: "Private or local address not allowed." };
  }

  const domainResult = validateDomain(host);
  if (!domainResult.ok) {
    return { valid: false, error: domainResult.error };
  }

  const defaultPort = DEFAULT_PORTS[url.protocol];
  let netloc = domainResult.normalized!;
  if (url.port && url.port !== defaultPort) {
    netloc = `${netloc}:${url.port}`;
  }

  let path = url.pathname || "";
  if (path === "/") path = "";

  const normalized = `${url.protocol}//${netloc}${path}${url.search}${url.hash}`;
  return { valid: true, normalized };
}

export function isWebsiteValid(raw: string): boolean {
  return validateWebsite(raw).valid;
}
